import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { getCreatorProfile, formatSignalMoney } from './signalDataFeeds';
import './SignalsMarketplacePage.css';

// Creator profile — every public signal one person runs, in one place.
//
// The deployment counter is the important part. The obvious way to game a signal marketplace is
// to deploy fifty variants and advertise whichever one got lucky, and no amount of per-signal
// verification catches that. Rather than trying to prohibit it, this states plainly how many
// deployments a creator has and how many they keep private — a reader can then judge for
// themselves whether one good chart out of forty means anything.

function CreatorProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getCreatorProfile(userId).then(setData).catch((err) => setError(err.message));
  }, [userId]);

  if (error) {
    return (
      <div className="market-page page-shell">
        <p className="signal-error">{error}</p>
        <button type="button" className="ghost-button compact-button" onClick={() => navigate('/signals')}>
          Back to signals
        </button>
      </div>
    );
  }
  if (!data) return <div className="market-page page-shell"><p className="muted-text">Loading…</p></div>;

  const { creator, signals, deploymentCounts } = data;

  return (
    <HelmetProvider>
      <div className="market-page page-shell">
        <Helmet><title>{creator.name} — Signal Creator | Vix Algo Trading</title></Helmet>

        <header className="creator-head">
          <button type="button" className="signal-back" onClick={() => navigate('/signals')}>
            ← All signals
          </button>
          <h1>{creator.name}</h1>
          <p className="muted-text">
            Member since {new Date(creator.memberSince).toLocaleDateString()} ·
            {' '}{signals.length} public signal{signals.length === 1 ? '' : 's'}
          </p>
          <div className="creator-transparency">
            <strong>{deploymentCounts.total}</strong> deployment{deploymentCounts.total === 1 ? '' : 's'} total ·
            <strong>{deploymentCounts.public}</strong> public ·
            <strong>{deploymentCounts.private}</strong> private
            <span>— shown so you can judge a track record in context.</span>
          </div>
        </header>

        {signals.length === 0 ? (
          <div className="market-empty">
            <h3>No public signals</h3>
            <p className="muted-text">This creator has not shared any signals publicly.</p>
          </div>
        ) : (
          <div className="market-grid">
            {signals.map(({ deployment, stats, badges, daysLive, meetsRankingBar }) => (
              <article className="market-card" key={deployment.id}>
                <header>
                  <div>
                    <h3>{deployment.name}</h3>
                    <span className="muted-text" style={{ fontSize: '0.78rem' }}>
                      {deployment.symbol} · {deployment.timeframe} · {daysLive}d live
                    </span>
                  </div>
                  {meetsRankingBar
                    ? <span className="signal-pill ok">Verified</span>
                    : <span className="signal-pill">Proving</span>}
                </header>
                <div className="market-metrics">
                  <div>
                    <span>Return</span>
                    <strong className={stats.returnPct >= 0 ? 'up' : 'down'}>
                      {stats.returnPct >= 0 ? '+' : ''}{stats.returnPct.toFixed(1)}%
                    </strong>
                  </div>
                  <div><span>Win rate</span><strong>{stats.trades ? `${stats.winRate.toFixed(0)}%` : '—'}</strong></div>
                  <div><span>Signals</span><strong>{stats.trades}</strong></div>
                  <div><span>Max DD</span><strong>{stats.maxDrawdownPct.toFixed(1)}%</strong></div>
                </div>
                {badges.length > 0 && (
                  <div className="market-badges">
                    {badges.slice(0, 3).map((badge) => (
                      <span key={badge.id} className="market-badge">{badge.label}</span>
                    ))}
                  </div>
                )}
                <footer>
                  <span className="muted-text">Net {formatSignalMoney(stats.netProfit)}</span>
                  <button
                    type="button"
                    className="primary-cta compact-button"
                    onClick={() => navigate(`/signals/${deployment.id}`)}
                  >
                    View
                  </button>
                </footer>
              </article>
            ))}
          </div>
        )}
      </div>
    </HelmetProvider>
  );
}

export default CreatorProfilePage;
